import { useCallback, useRef, useState } from 'react';
import { useAppDispatch, useAppSelector } from '@/state/store';
import { setScanId, setScanResult } from '@/state/slices/onboardingSlice';
import {
  postProcessFilesBatch,
  type AstAnalysis,
  type ProcessFilesSummary,
  type ProcessFilesResponse,
} from '@/components/smartai/api/processFiles';
import { assertScmProvider } from '@/types/scm-provider';
import { getApiErrorMessage } from '@/utils';

const BATCH_SIZE = 300;

export interface BatchProgress {
  currentBatch: number;
  totalBatches: number;
  percentage: number;
  processedFiles: number;
  totalFiles: number;
}

const aggregateResults = (
  results: ProcessFilesResponse[],
): { astAnalysis: AstAnalysis; summary: ProcessFilesSummary } => {
  const ast: AstAnalysis = {
    totalFiles: 0,
    totalLines: 0,
    totalMethods: 0,
    totalFunctions: 0,
    totalClasses: 0,
    totalNonExecutableLines: 0,
    totalExecutableLines: 0,
    totalBranches: 0,
    totalLoops: 0,
    fileDetails: [],
  };
  for (const r of results) {
    const a = r.data.astAnalysis;
    ast.totalFiles += a.totalFiles ?? 0;
    ast.totalLines += a.totalLines ?? 0;
    ast.totalMethods += a.totalMethods ?? 0;
    ast.totalFunctions += a.totalFunctions ?? 0;
    ast.totalClasses += a.totalClasses ?? 0;
    ast.totalNonExecutableLines += a.totalNonExecutableLines ?? 0;
    ast.totalExecutableLines += a.totalExecutableLines ?? 0;
    ast.totalBranches += a.totalBranches ?? 0;
    ast.totalLoops += a.totalLoops ?? 0;
    if (a.fileDetails) ast.fileDetails!.push(...a.fileDetails);
  }
  const summary: ProcessFilesSummary = {
    filesProcessed: ast.totalFiles,
    totalLinesOfCode: ast.totalLines,
    totalMethods: ast.totalMethods,
    totalFunctions: ast.totalFunctions,
    totalClasses: ast.totalClasses,
    batchesProcessed: results.length,
  };
  return { astAnalysis: ast, summary };
};

type UseProcessFilesScanOptions = {
  onComplete?: (elapsedTime: string) => void;
};

export function useProcessFilesScan(options: UseProcessFilesScanOptions = {}) {
  const dispatch = useAppDispatch();
  const {
    scmProvider,
    files,
    patToken,
    repoUrl,
    branchName,
    pipelineName,
    azureProject,
    repoId,
    branchId,
    prEmails,
    autoPrEmails,
    isFrontend,
  } = useAppSelector(state => state.onboarding);

  const [processing, setProcessing] = useState(false);
  const [progress, setProgress] = useState<BatchProgress>({
    currentBatch: 0,
    totalBatches: 0,
    percentage: 0,
    processedFiles: 0,
    totalFiles: 0,
  });
  const [elapsedTime, setElapsedTime] = useState('00:00:00');
  const [error, setError] = useState<string | null>(null);

  const timerIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const startTimeRef = useRef<Date | null>(null);
  const onCompleteRef = useRef(options.onComplete);
  onCompleteRef.current = options.onComplete;

  const stopTimer = useCallback(() => {
    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
      timerIntervalRef.current = null;
    }
  }, []);

  const startTimer = useCallback(() => {
    startTimeRef.current = new Date();
    setElapsedTime('00:00:00');
    timerIntervalRef.current = setInterval(() => {
      if (!startTimeRef.current) return;
      const elapsedMs = Date.now() - startTimeRef.current.getTime();
      const hours = Math.floor(elapsedMs / 3600000);
      const minutes = Math.floor((elapsedMs % 3600000) / 60000);
      const seconds = Math.floor((elapsedMs % 60000) / 1000);
      setElapsedTime(
        `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`,
      );
    }, 1000);
  }, []);

  const processFiles = useCallback(async () => {
    if (!scmProvider || files.length === 0) {
      setError('Scan inputs are missing. Close and try onboarding again.');
      return;
    }
    if (!repoId?.trim() || !branchId?.trim()) {
      setError(
        'Repository was not onboarded correctly (missing IDs). Close and try onboarding again.',
      );
      return;
    }

    setProcessing(true);
    setError(null);
    startTimer();

    const totalFiles = files.length;
    const batches = [];
    for (let i = 0; i < files.length; i += BATCH_SIZE) {
      batches.push(files.slice(i, i + BATCH_SIZE));
    }
    const totalBatches = batches.length || 1;
    const reviewers = [...prEmails, ...autoPrEmails];
    const provider = assertScmProvider(scmProvider);
    let finalElapsed = '00:00:00';

    try {
      let scanId: string | null = null;
      const batchResults: ProcessFilesResponse[] = [];

      for (let i = 0; i < batches.length; i++) {
        const batchNumber = i + 1;
        const processedSoFar = i * BATCH_SIZE;

        setProgress({
          currentBatch: batchNumber,
          totalBatches,
          percentage: Math.round((processedSoFar / totalFiles) * 100),
          processedFiles: processedSoFar,
          totalFiles,
        });

        const result = await postProcessFilesBatch(
          provider,
          {
            repoUrl,
            branchName,
            files: batches[i],
            reviewers,
            batchNumber,
            totalBatches,
            pipeline_name: pipelineName,
            azure_project: azureProject,
            repoId,
            branchId,
            scanId,
            isFrontend,
          },
          patToken,
        );

        if (batchNumber === 1) {
          scanId = result.data.scanId;
        }
        batchResults.push(result);

        if (i < batches.length - 1) {
          await new Promise<void>(resolve => setTimeout(resolve, 1000));
        }
      }

      setProgress(prev => ({
        ...prev,
        percentage: 100,
        processedFiles: totalFiles,
      }));

      dispatch(setScanResult(aggregateResults(batchResults)));

      if (scanId) {
        dispatch(setScanId(scanId));
      }

      if (startTimeRef.current) {
        const elapsedMs = Date.now() - startTimeRef.current.getTime();
        const minutes = Math.floor(elapsedMs / 60000);
        const seconds = Math.floor((elapsedMs % 60000) / 1000);
        finalElapsed = `${minutes}:${String(seconds).padStart(2, '0')}`;
      }

      onCompleteRef.current?.(finalElapsed);
    } catch (err) {
      setError(getApiErrorMessage(err, 'Failed to process files'));
    } finally {
      setProcessing(false);
      stopTimer();
      if (startTimeRef.current) {
        const elapsedMs = Date.now() - startTimeRef.current.getTime();
        const minutes = Math.floor(elapsedMs / 60000);
        const seconds = Math.floor((elapsedMs % 60000) / 1000);
        finalElapsed = `${minutes}:${String(seconds).padStart(2, '0')}`;
        setElapsedTime(
          `00:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`,
        );
      }
    }
  }, [
    scmProvider,
    files,
    patToken,
    repoUrl,
    branchName,
    pipelineName,
    azureProject,
    repoId,
    branchId,
    prEmails,
    autoPrEmails,
    isFrontend,
    dispatch,
    startTimer,
    stopTimer,
  ]);

  return {
    processFiles,
    processing,
    progress,
    elapsedTime,
    error,
    filesCount: files.length,
  };
}
